import {engineContext} from './context.js';

(function(global){'use strict';
const VERSION='0.1.0-alpha';

function arr(x){ return Array.isArray(x)?x:[]; }
function uniq(x){ return [...new Set(arr(x).filter(Boolean))]; }

function audit(input){
  input=input||{};
  const issues=[];

  const snapshot=input.snapshot||null;
  const reopenTasks=arr(input.reopenTasks);
  const invalidations=arr(input.invalidations);
  const brief=input.brief||null;
  const lineage=input.lineage||null;

  if(snapshot){
    const inv=uniq(snapshot.state&&snapshot.state.invalidatedConclusions);
    const taskConclusionIds=uniq(reopenTasks.flatMap(t=>arr(t.conclusionIds)));
    for(const c of inv){
      if(!taskConclusionIds.includes(c)){
        issues.push({code:'INVALIDATION_WITHOUT_REOPEN_TASK',severity:'REVIEW',ref:c});
      }
    }
  }

  if(invalidations.length && reopenTasks.length===0){
    const actionable=invalidations.some(x=>['INVALIDATE','REVIEW_CONFLICT','REVIEW_EVIDENCE','REVALIDATE'].includes(x.action));
    if(actionable) issues.push({code:'ACTIONABLE_INVALIDATION_WITHOUT_TASKS',severity:'REVIEW'});
  }

  if(brief){
    if(brief.readiness==='READY_FOR_DECISION' && invalidations.some(x=>x.action==='INVALIDATE')){
      issues.push({code:'READY_WHILE_CONCLUSION_INVALIDATED',severity:'ERROR'});
    }
    if(brief.tradeSignal===true) issues.push({code:'TRADE_SIGNAL_FORBIDDEN',severity:'ERROR'});
    if(brief.capitalPermission===true) issues.push({code:'CAPITAL_PERMISSION_FORBIDDEN',severity:'ERROR'});
  }

  if(lineage && lineage.conclusions){
    const claimIds=new Set(arr(lineage.claims).map(x=>x.claimId||x.id).filter(Boolean));
    for(const c of arr(lineage.conclusions)){
      const missing=arr(c.claimIds).filter(id=>!claimIds.has(id));
      if(missing.length) issues.push({code:'CONCLUSION_REFERENCES_MISSING_CLAIM',severity:'ERROR',ref:c.conclusionId||c.id,missing});
    }
  }

  if(snapshot && brief && snapshot.state){
    const sReady=snapshot.state.readiness;
    if(sReady && brief.readiness && sReady!==brief.readiness){
      issues.push({code:'READINESS_STATE_DRIFT',severity:'REVIEW',snapshot:sReady,brief:brief.readiness});
    }
  }

  const severityRank={INFO:0,REVIEW:1,ERROR:2};
  const maxSeverity=issues.reduce((m,x)=>severityRank[x.severity]>severityRank[m]?x.severity:m,'INFO');

  return Object.freeze({
    valid:!issues.some(x=>x.severity==='ERROR'),
    reviewRequired:issues.some(x=>x.severity==='REVIEW'),
    maxSeverity,
    issues:Object.freeze(issues),
    tradeSignal:false,
    capitalPermission:false
  });
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const clean=audit({
    snapshot:{state:{invalidatedConclusions:[],readiness:'REVIEW'}},
    reopenTasks:[],
    invalidations:[],
    brief:{readiness:'REVIEW',tradeSignal:false,capitalPermission:false},
    lineage:{claims:[{claimId:'c1'}],conclusions:[{conclusionId:'k1',claimIds:['c1']}]}
  });
  ok('clean payload is valid',clean.valid===true);
  ok('clean payload needs no review',clean.reviewRequired===false);

  const a1=audit({
    snapshot:{state:{invalidatedConclusions:['k1'],readiness:'REVIEW'}},
    reopenTasks:[],
    invalidations:[{conclusionId:'k1',action:'INVALIDATE'}]
  });
  ok('missing reopen task raises review',a1.reviewRequired===true);
  ok('actionable invalidation without tasks flagged',a1.issues.some(x=>x.code==='ACTIONABLE_INVALIDATION_WITHOUT_TASKS'));

  const a2=audit({
    invalidations:[{conclusionId:'k1',action:'INVALIDATE'}],
    brief:{readiness:'READY_FOR_DECISION',tradeSignal:false,capitalPermission:false}
  });
  ok('ready while invalidated is invalid',a2.valid===false);
  ok('ready while invalidated flagged',a2.issues.some(x=>x.code==='READY_WHILE_CONCLUSION_INVALIDATED'));

  const a3=audit({
    brief:{readiness:'REVIEW',tradeSignal:true,capitalPermission:false}
  });
  ok('trade signal true invalidates audit',a3.valid===false);
  ok('trade signal violation flagged',a3.issues.some(x=>x.code==='TRADE_SIGNAL_FORBIDDEN'));

  const a4=audit({
    lineage:{claims:[],conclusions:[{conclusionId:'k1',claimIds:['missing']}]}
  });
  ok('missing lineage claim invalidates audit',a4.valid===false);
  ok('missing lineage claim flagged',a4.issues.some(x=>x.code==='CONCLUSION_REFERENCES_MISSING_CLAIM'));

  const a5=audit({
    snapshot:{state:{invalidatedConclusions:[],readiness:'BLOCKED'}},
    brief:{readiness:'REVIEW',tradeSignal:false,capitalPermission:false}
  });
  ok('readiness drift raises review',a5.reviewRequired===true);
  ok('readiness drift not hard error',a5.valid===true);

  const a6=audit({
    snapshot:{state:{invalidatedConclusions:['k1'],readiness:'REVIEW'}},
    reopenTasks:[{taskId:'t1',conclusionIds:['k1']}],
    invalidations:[{conclusionId:'k1',action:'INVALIDATE'}],
    brief:{readiness:'REVIEW',tradeSignal:false,capitalPermission:false}
  });
  ok('matching invalidation and reopen task avoids orphan warning',!a6.issues.some(x=>x.code==='INVALIDATION_WITHOUT_REOPEN_TASK'));

  ok('audit never emits trade signal',clean.tradeSignal===false);
  ok('audit never emits capital permission',clean.capitalPermission===false);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,audit,selfTest};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestCrossEngineConsistencyAuditor=Object.freeze(API);
})(engineContext);